import {
  getTotalYearsExperienceFromFirstJob,
  type PreviousJobLike,
} from "@/domains/auth/onboarding/components/utils";
import type { OnboardingStore } from "@/domains/auth/onboarding/context/OnboardingContext";
import type { UpdateProfileV2 } from "@/domains/freelance/profile/api/types";

/**
 * The `profile` of the `POST /profile/onboarding` body: the `/me` snapshot of
 * the store, then the fields of the form.
 *
 * The three taxonomy fields always come from the `sector` step, written after
 * the spread so `/me` never reaches the wire (freelancer-categories D6, AC19):
 * `/me` reads `job_subcategories` as `{ key, status }`, which the backend
 * rejects with a 400. The store sends the keys only, `[]` included: the
 * freelancer saw the list on the step, so a suggestion they removed is a
 * rejection. This replaces taxonomy-contract-pr3 D5, which left the field out
 * and passed the secondary of `/me` through.
 *
 * @param jobs - The jobs the experience years are counted from. The route
 * passes the ones it sends in the same body; by default, those of the store.
 */
export function buildOnboardingProfile(
  store: OnboardingStore,
  jobs: PreviousJobLike[] = store.experience.value,
) {
  const { avatar_url: _, ...rest } = store.info.value;
  const sector = store.sector.value;

  return {
    ...store.profile,
    ...rest,
    birthdate: store.info.value.date_of_birth,
    residency: store.info.value.residency || null,
    job_category: sector.job_category,
    job_category_secondary: sector.job_category_secondary,
    job_subcategories: sector.job_subcategories.map(({ key }) => key),
    skills: store.skills.value.join(","),
    job_title: store.job_title.value,
    languages: store.locale.value.spoken_languages.reduce(
      (acc, curr) => {
        acc[curr.code] = curr.level;
        return acc;
      },
      {} as Record<string, string>,
    ),
    bio: store.summary.value,
    full_name: store.profile.full_name!,
    price_per_day: store.pricing.value.price_per_day.toString(),
    website: store.online_presence.value.website || null,
    portfolio: store.online_presence.value.portfolio || null,
    phone_number: store.info.value.phone_number || null,
    social_media: {
      github: store.online_presence.value.github || null,
      linkedin: store.online_presence.value.linkedin || null,
      instagram: store.online_presence.value.instagram || null,
    },
    weekly_hours: store.info.value.weekly_hours ?? "40",
    available_from: store.info.value.is_available ? new Date() : null,
    total_experience_years: getTotalYearsExperienceFromFirstJob(jobs),
  } satisfies UpdateProfileV2;
}
